import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Fab,
  Tooltip,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ChatBubbleOutline as CommentIcon,
  Send as SendIcon,
  CameraAlt as CameraIcon,
  PhotoLibrary as GalleryIcon,
  FlashOn as QuickIcon,
  CalendarToday as CalendarIcon,
  LibraryAdd as BatchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';
import { Divider, List, ListItem, ListItemText } from '@mui/material';
import useStore from '../store/useStore';

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

export default function MultimediaPortfolio({ studentId, isReadOnly }) {
  const en = useStore((s) => s.language) === 'en';
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // Edit, View, Comments
  const [editItem, setEditItem] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [viewImage, setViewImage] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [activeCommentBox, setActiveCommentBox] = useState(null);

  // Date taken (for regular upload + edit)
  const [takenAt, setTakenAt] = useState(todayISO());
  const [editTakenAt, setEditTakenAt] = useState('');

  // Quick-add from camera/phone
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickFile, setQuickFile] = useState(null);
  const [quickPreview, setQuickPreview] = useState(null);
  const [quickTitle, setQuickTitle] = useState('');
  const [quickTakenAt, setQuickTakenAt] = useState(todayISO());
  const [quickUploading, setQuickUploading] = useState(false);

  // Batch upload
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchDate, setBatchDate] = useState(todayISO());
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0); // 0..batchFiles.length
  const batchInputRef = useRef(null);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    loadPortfolio();
  }, [studentId]);

  const loadPortfolio = () => {
    fetch(`/api/students/${studentId}`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => setAssets(data.portfolio || []))
      .catch((err) => console.error('Error loading portfolio:', err));
  };

  const handleAddComment = async (assetId) => {
    if (!newComment.trim()) return;
    try {
      const res = await fetch(
        `/api/students/${studentId}/portfolio/${assetId}/comments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: newComment }),
        }
      );
      if (res.ok) {
        setNewComment('');
        setActiveCommentBox(null);
        loadPortfolio();
      }
    } catch (err) {
      console.error('Comment failed:', err);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('takenAt', takenAt || todayISO());

    try {
      const res = await fetch(`/api/students/${studentId}/portfolio`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        setOpen(false);
        setFile(null);
        setTitle('');
        setDescription('');
        setTakenAt(todayISO());
        loadPortfolio();
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleBatchUpload = async () => {
    if (!batchFiles.length) return;
    setBatchUploading(true);
    setBatchProgress(0);
    let done = 0;
    for (const f of batchFiles) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('title', f.name.replace(/\.[^.]+$/, ''));
      fd.append('description', '');
      fd.append('takenAt', batchDate || todayISO());
      try {
        await fetch(`/api/students/${studentId}/portfolio`, {
          method: 'POST',
          body: fd,
        });
      } catch (e) {
        /* continue even if one fails */
      }
      done++;
      setBatchProgress(done);
    }
    setBatchUploading(false);
    setBatchOpen(false);
    setBatchFiles([]);
    setBatchDate(todayISO());
    setBatchProgress(0);
    loadPortfolio();
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const res = await fetch(
        `/api/students/${studentId}/portfolio/${assetId}`,
        {
          method: 'DELETE',
        }
      );
      if (res.ok) loadPortfolio();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleUpdateMetadata = async () => {
    try {
      const res = await fetch(
        `/api/students/${studentId}/portfolio/${editItem._id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title,
            description,
            takenAt: editTakenAt || null,
          }),
        }
      );
      if (res.ok) {
        setOpenEdit(false);
        setEditItem(null);
        setTitle('');
        setDescription('');
        setEditTakenAt('');
        loadPortfolio();
      }
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const handleQuickFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setQuickFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setQuickPreview(ev.target.result);
    reader.readAsDataURL(f);
    setQuickTitle('');
    setQuickOpen(true);
  };

  const handleQuickUpload = async () => {
    if (!quickFile) return;
    setQuickUploading(true);
    const formData = new FormData();
    formData.append('file', quickFile);
    formData.append(
      'title',
      quickTitle || quickFile.name.replace(/\.[^.]+$/, '')
    );
    formData.append('description', '');
    formData.append('takenAt', quickTakenAt || todayISO());
    try {
      const res = await fetch(`/api/students/${studentId}/portfolio`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        setQuickOpen(false);
        setQuickFile(null);
        setQuickPreview(null);
        setQuickTitle('');
        setQuickTakenAt(todayISO());
        loadPortfolio();
      }
    } catch (err) {
      console.error('Quick upload failed:', err);
    } finally {
      setQuickUploading(false);
    }
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {en ? 'Multimedia Portfolio' : 'Портфолио'}
        </Typography>
        {!isReadOnly && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Tooltip title="Сфотографировать или выбрать из галереи">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CameraIcon />}
                onClick={() => cameraInputRef.current?.click()}
                sx={{ borderRadius: 3 }}
                size="small"
              >
                Быстро
              </Button>
            </Tooltip>
            <Tooltip title="Загрузить несколько файлов за одну неделю">
              <Button
                variant="outlined"
                startIcon={<BatchIcon />}
                onClick={() => setBatchOpen(true)}
                sx={{ borderRadius: 3 }}
                size="small"
              >
                За неделю
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{ borderRadius: 3 }}
              size="small"
            >
              Загрузить
            </Button>
          </Stack>
        )}
      </Box>

      {/* Hidden file inputs for camera / gallery */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleQuickFileSelect}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,video/*,application/pdf"
        style={{ display: 'none' }}
        onChange={handleQuickFileSelect}
      />

      {assets.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 4,
            bgcolor: 'grey.50',
          }}
        >
          <Typography color="text.secondary">
            No multimedia work added yet.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {assets.map((asset, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  height: '100%',
                }}
              >
                {asset.type === 'image' ? (
                  <CardMedia
                    component="img"
                    height="180"
                    image={asset.url}
                    alt={asset.title}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 180,
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption">
                      {asset.type.toUpperCase()}
                    </Typography>
                  </Box>
                )}
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {asset.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {asset.description}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: 11, color: 'text.disabled' }}
                      />
                      <Typography variant="caption" color="text.disabled">
                        {new Date(
                          asset.takenAt || asset.uploadedAt
                        ).toLocaleDateString('ru-RU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setActiveCommentBox(
                            activeCommentBox === asset._id ? null : asset._id
                          )
                        }
                      >
                        <CommentIcon fontSize="small" />
                      </IconButton>
                      {asset.type === 'image' && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setViewImage(asset)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      )}
                      {!isReadOnly && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditItem(asset);
                              setTitle(asset.title);
                              setDescription(asset.description);
                              const d = asset.takenAt || asset.uploadedAt;
                              setEditTakenAt(
                                d
                                  ? new Date(d).toISOString().split('T')[0]
                                  : todayISO()
                              );
                              setOpenEdit(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(asset._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Comments Section */}
                  {(activeCommentBox === asset._id ||
                    (asset.comments && asset.comments.length > 0)) && (
                    <Box
                      sx={{ mt: 2, bgcolor: 'grey.50', p: 1, borderRadius: 2 }}
                    >
                      {asset.comments && asset.comments.length > 0 && (
                        <List dense>
                          {asset.comments.map((c, ci) => (
                            <ListItem key={ci} disableGutters sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 1,
                                      alignItems: 'baseline',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 800,
                                        color:
                                          c.authorModel === 'Teacher'
                                            ? 'primary.main'
                                            : 'secondary.main',
                                      }}
                                    >
                                      {c.authorModel}:
                                    </Typography>
                                    <Typography variant="body2">
                                      {c.text}
                                    </Typography>
                                  </Box>
                                }
                                secondary={new Date(
                                  c.createdAt
                                ).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                      {activeCommentBox === asset._id && (
                        <Box sx={{ display: 'flex', mt: 1, gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Add a comment..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            sx={{ bgcolor: 'white' }}
                          />
                          <IconButton
                            color="primary"
                            onClick={() => handleAddComment(asset._id)}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Загрузить работу</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Название"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Описание"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <TextField
              fullWidth
              type="date"
              label="Дата работы"
              value={takenAt}
              onChange={(e) => setTakenAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Можно указать прошедшую дату"
              inputProps={{ max: todayISO() }}
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
            >
              {file ? file.name : 'Выбрать файл (фото / видео / PDF)'}
              <input
                type="file"
                hidden
                onChange={(e) => setFile(e.target.files[0])}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Загрузить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Metadata Dialog */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Редактировать</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Название"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Описание"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <TextField
              fullWidth
              type="date"
              label="Дата работы"
              value={editTakenAt}
              onChange={(e) => setEditTakenAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayISO() }}
              helperText="Исправьте дату если нужно"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenEdit(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleUpdateMetadata}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image View Popup */}
      <Dialog
        open={Boolean(viewImage)}
        onClose={() => setViewImage(null)}
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setViewImage(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <DeleteIcon />
          </IconButton>
          {viewImage && (
            <img
              src={viewImage.url}
              alt={viewImage.title}
              style={{
                width: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Typography variant="h6">{viewImage?.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {viewImage?.description}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Hidden batch file input */}
      <input
        ref={batchInputRef}
        type="file"
        multiple
        accept="image/*,video/*,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => setBatchFiles(Array.from(e.target.files || []))}
      />

      {/* ── Batch Upload Dialog ──────────────────────────────────────────── */}
      <Dialog
        open={batchOpen}
        onClose={() => {
          if (!batchUploading) setBatchOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <BatchIcon color="primary" /> Загрузка за неделю
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Выберите дату (например, первый день недели) и загрузите сразу
            несколько файлов — все получат эту дату.
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="Дата для всех файлов"
            value={batchDate}
            onChange={(e) => setBatchDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: todayISO() }}
            sx={{ mb: 2 }}
          />
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            fullWidth
            onClick={() => batchInputRef.current?.click()}
            disabled={batchUploading}
            sx={{ mb: 2 }}
          >
            {batchFiles.length > 0
              ? `Выбрано: ${batchFiles.length} файлов`
              : 'Выбрать файлы'}
          </Button>
          {batchFiles.length > 0 && (
            <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 1 }}>
              {batchFiles.map((f, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flexGrow: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {f.name}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {(f.size / 1024 / 1024).toFixed(1)} МБ
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={batchUploading}
                    onClick={() =>
                      setBatchFiles((prev) => prev.filter((_, j) => j !== i))
                    }
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          {batchUploading && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={(batchProgress / batchFiles.length) * 100}
                sx={{ borderRadius: 1 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}
              >
                {batchProgress} / {batchFiles.length} загружено
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setBatchOpen(false);
              setBatchFiles([]);
            }}
            disabled={batchUploading}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleBatchUpload}
            disabled={!batchFiles.length || !batchDate || batchUploading}
            startIcon={
              batchUploading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <BatchIcon />
              )
            }
          >
            {batchUploading
              ? 'Загрузка...'
              : `Загрузить ${batchFiles.length || ''} файлов`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick Add Dialog (camera / gallery) ──────────────────────────── */}
      <Dialog
        open={quickOpen}
        onClose={() => {
          setQuickOpen(false);
          setQuickFile(null);
          setQuickPreview(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuickIcon color="secondary" />
            Quick Add Work
          </Box>
        </DialogTitle>
        <DialogContent>
          {quickPreview && (
            <Box
              sx={{
                width: '100%',
                maxHeight: 260,
                overflow: 'hidden',
                borderRadius: 2,
                mb: 2,
                bgcolor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {quickFile?.type?.startsWith('video') ? (
                <video
                  src={quickPreview}
                  controls
                  style={{ maxWidth: '100%', maxHeight: 260 }}
                />
              ) : (
                <img
                  src={quickPreview}
                  alt="preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 260,
                    objectFit: 'contain',
                  }}
                />
              )}
            </Box>
          )}
          <TextField
            fullWidth
            autoFocus
            label="Название (необязательно)"
            placeholder={quickFile?.name?.replace(/\.[^.]+$/, '') || ''}
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            size="small"
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Дата"
            size="small"
            value={quickTakenAt}
            onChange={(e) => setQuickTakenAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: todayISO() }}
            sx={{ mb: 1.5 }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<CameraIcon />}
              variant="outlined"
              onClick={() => cameraInputRef.current?.click()}
              fullWidth
            >
              Retake
            </Button>
            <Button
              size="small"
              startIcon={<GalleryIcon />}
              variant="outlined"
              onClick={() => galleryInputRef.current?.click()}
              fullWidth
            >
              Gallery
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setQuickOpen(false);
              setQuickFile(null);
              setQuickPreview(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleQuickUpload}
            disabled={!quickFile || quickUploading}
            startIcon={
              quickUploading ? <CircularProgress size={16} /> : <QuickIcon />
            }
          >
            {quickUploading ? 'Saving…' : 'Add to Portfolio'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
